/**
 * SORT · SEARCH · RATING, for any list of reviews.
 *
 * The product reviews sheet grew these three controls first; the shop's own
 * reviews need exactly the same three, over rows whose columns are named
 * differently (`stars`/`createdAt`/`body` there, `rating`/`reviewedAt`/`text`
 * here). Copying two hundred lines would have given this codebase two
 * answers to "what does Rating: High → Low mean", so the mechanics live here
 * once and the callers hand in three accessors.
 *
 * WHAT THIS HOOK OWNS AND WHAT IT DOES NOT. It owns the sort key, the query
 * and the set of ticked stars — the state both lists have. It does NOT own
 * the caller's own filters (With photos, a variant, a SOURCE): those differ
 * per list, so the caller keeps them and passes a predicate. `clearAll`
 * clears the caller's too, through `onClearExtra`, because a Clear chip that
 * leaves one filter standing is worse than no Clear chip.
 *
 * `stars` is a SET, not a value: "5 or 1" is a genuinely different question
 * from "5", and the reference lets a shopper ask it.
 */
import { useCallback, useMemo, useState } from 'react';
import { textMatches } from './reviewsFiltering';

export default function useReviewsControls({
  rows,
  sortOptions,
  defaultSort,
  /** (row) => the 1–5 rating on it. */
  getStars,
  /** (row) => the array of strings the search may look inside. */
  getSearchParts,
  /** Optional (row) => boolean, the caller's own filters. */
  extraFilter = null,
  /** Are any of the caller's own filters on? Feeds `filtersActive`. */
  extraActive = false,
  /** Called by `clearAll` so the caller's own filters clear with ours. */
  onClearExtra = null,
}) {
  const [sort, setSort] = useState(defaultSort);
  const [query, setQuery] = useState('');
  const [stars, setStars] = useState(() => new Set());

  const activeSort = sortOptions.find((s) => s.value === sort) || sortOptions[0];

  const visible = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const out = list.filter((r) => {
      if (stars.size > 0 && !stars.has(Math.round(Number(getStars(r))))) return false;
      if (extraFilter && !extraFilter(r)) return false;
      return textMatches(getSearchParts(r), query);
    });
    /* Ordering the server could not give us. `slice` first — `sort` mutates,
     * and mutating the memo's input array reorders `rows` behind React's
     * back. */
    return activeSort && activeSort.compare ? out.slice().sort(activeSort.compare) : out;
  }, [rows, stars, query, activeSort, extraFilter, getStars, getSearchParts]);

  const toggleStar = useCallback((s) => {
    setStars((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  }, []);

  const resetStars = useCallback(() => setStars(new Set()), []);

  const clearAll = useCallback(() => {
    setStars(new Set());
    setQuery('');
    if (onClearExtra) onClearExtra();
  }, [onClearExtra]);

  const filtersActive = stars.size > 0 || !!query || !!extraActive;

  return {
    sort, setSort, activeSort,
    query, setQuery,
    stars, toggleStar, resetStars,
    clearAll, filtersActive,
    visible,
  };
}
